import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/** Duration by severity — errors stay longer so users can read them. */
const DURATION_MAP: Record<ToastType, number> = {
    success: 3000,
    info: 4000,
    warning: 5000,
    error: 6000,
};

const ICON_MAP: Record<ToastType, React.ReactNode> = {
    success: <CheckCircle2 size={18} />,
    error: <AlertCircle size={18} />,
    warning: <AlertTriangle size={18} />,
    info: <Info size={18} />,
};

type ToastProps = {
    message: string;
    type: ToastType;
    onClose: () => void;
    duration?: number;
};

export function Toast({ message, type, onClose, duration }: ToastProps) {
    const [visible, setVisible] = useState(true);
    const effectiveDuration = duration ?? DURATION_MAP[type];

    useEffect(() => {
        const timer = setTimeout(() => {
            setVisible(false);
            setTimeout(onClose, 300);
        }, effectiveDuration);
        return () => clearTimeout(timer);
    }, [effectiveDuration, onClose]);

    const dismiss = () => { setVisible(false); setTimeout(onClose, 300); };

    return (
        <div className={`toast toast-${type} ${visible ? 'toast-enter' : 'toast-exit'}`} role="alert">
            <span className="toast-icon" aria-hidden="true">
                {ICON_MAP[type]}
            </span>
            <span className="toast-message">{message}</span>
            <button
                type="button"
                className="toast-close"
                onClick={dismiss}
                aria-label="Dismiss notification"
            >
                <X size={16} />
            </button>
        </div>
    );
}

/** Container to render toasts at the top of the screen */
export function ToastContainer({ toasts, removeToast }: {
    toasts: { id: number; message: string; type: ToastType }[];
    removeToast: (id: number) => void;
}) {
    if (toasts.length === 0) return null;
    return (
        <div
            className="toast-container"
            role="status"
            aria-live="polite"
            aria-atomic="false"
        >
            {toasts.map(t => (
                <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
            ))}
        </div>
    );
}

/** Hook to manage toasts */
let toastId = 0;
 
export function useToast() {
    const [toasts, setToasts] = useState<{ id: number; message: string; type: ToastType }[]>([]);

    const addToast = (message: string, type: ToastType = 'success') => {
        const id = ++toastId;
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return { toasts, addToast, removeToast };
}
